import { HelpSidebarKey } from '@lemontaps/lib/constants';
import { Box, BoxProps, Card } from '@mui/material';
import { HelpButton } from '../HelpSidebar';
import LtActionButtonBar, { LtActionButtonBarProps } from '../LtActionButtonBar';
import { useMemo } from 'react';
import useDesktopView from '@/infrastructure/hooks/useDesktopView';
import { RequireOnlyOne } from '@lemontaps/lib/types/util';
import { LtTabsProps } from '../LtTabs';
import LtTabs from '../LtTabs';
import {
  LeftSideSheet,
  LeftSideSheetProps,
  useLeftSidesheetWidth,
} from './components/LeftSideSheet';
import { CSS_VARS } from '@/infrastructure/constants';

type Props = RequireOnlyOne<
  {
    children: React.ReactNode;
    tabs?: LtTabsProps;
    maxWidth?: 's' | 'm' | 'l' | 'xl';
    helpSidebarConfigKey?: HelpSidebarKey | null;
    bottomActionBar?: LtActionButtonBarProps;
    topActionBar?: LtActionButtonBarProps;
    whiteCardWrapper?: boolean;
    leftSideSheet?: LeftSideSheetProps;
    fixedFullHeight?: boolean;
    rightActions?: React.ReactNode;
    leftActions?: React.ReactNode;
  },
  'children' | 'tabs'
> &
  BoxProps;

const maxWidthMap = {
  s: '60rem',
  m: '120rem',
  l: '180rem',
  xl: '240rem',
};

const paddings = {
  px: { xs: 2, md: 4 },
  pt: { xs: 2, md: 4.5 },
  pb: 4.5,
};

const PageContainer: React.FC<Props> = ({
  children,
  maxWidth,
  helpSidebarConfigKey,
  bottomActionBar,
  topActionBar,
  tabs,
  whiteCardWrapper,
  sx,
  leftSideSheet,
  fixedFullHeight,
  rightActions,
  leftActions,
  ...rest
}) => {
  const leftSidesheetWidth = useLeftSidesheetWidth();
  const { desktopView } = useDesktopView();
  const _sx = {
    ...(maxWidth
      ? { maxWidth: maxWidthMap[maxWidth as keyof typeof maxWidthMap], margin: '0 auto' }
      : {}),
    ...sx,
  };

  const sidesheetVisible = Boolean(desktopView && leftSideSheet);
  const SidesheetWrapper = useMemo(
    () =>
      sidesheetVisible
        ? ({ children }: { children: React.ReactNode }) => (
            <>
              <LeftSideSheet {...leftSideSheet} />
              <Box ml={leftSidesheetWidth}>{children}</Box>
            </>
          )
        : ({ children }: { children: React.ReactNode }) => <>{children}</>,
    [leftSideSheet, leftSidesheetWidth, sidesheetVisible],
  );

  const ContentWrapper = useMemo(
    () =>
      whiteCardWrapper && desktopView
        ? ({ children }: { children: React.ReactNode }) => (
            <Card
              sx={{
                p: 2,
                borderRadius: '1rem',
                height: fixedFullHeight
                  ? `calc(100vh - ${CSS_VARS.LT_DESKTOP_HEADER_HEIGHT_VAR})`
                  : undefined,
              }}
            >
              {children}
            </Card>
          )
        : ({ children }: { children: React.ReactNode }) => (
            <Box
              height={
                fixedFullHeight && desktopView
                  ? {
                      xs: `calc(100vh - ${CSS_VARS.LT_DESKTOP_HEADER_HEIGHT_VAR} - ${paddings.pt.xs}rem - ${paddings.pb}rem)`,
                      md: `calc(100vh - ${CSS_VARS.LT_DESKTOP_HEADER_HEIGHT_VAR} - ${paddings.pt.md}rem - ${paddings.pb}rem)`,
                    }
                  : undefined
              }
            >
              {children}
            </Box>
          ),
    [desktopView, fixedFullHeight, whiteCardWrapper],
  );

  return (
    <>
      {topActionBar && (
        <LtActionButtonBar
          location='top'
          renderRelativeBox
          disableBigButtonsOnMobile
          explicitOffsets={{ left: sidesheetVisible ? leftSidesheetWidth : undefined }}
          {...topActionBar}
        />
      )}
      <SidesheetWrapper>
        <Box sx={_sx} {...paddings} {...rest}>
          {(helpSidebarConfigKey || rightActions || leftActions) && (
            <Box display='flex' mt={-1.5} mb={2}>
              {leftActions && <Box mr={2}>{leftActions}</Box>}
              {(helpSidebarConfigKey || rightActions) && (
                <Box ml='auto' display='flex' alignItems='center'>
                  {rightActions && <Box mr={2}>{rightActions}</Box>}
                  {helpSidebarConfigKey && <HelpButton configKey={helpSidebarConfigKey} />}
                </Box>
              )}
            </Box>
          )}
          <ContentWrapper>{tabs ? <LtTabs {...tabs} /> : children}</ContentWrapper>
        </Box>
      </SidesheetWrapper>
      {bottomActionBar && (
        <LtActionButtonBar
          location='bottom'
          noCancelOnDesktop
          renderRelativeBox
          explicitOffsets={{ left: sidesheetVisible ? leftSidesheetWidth : undefined }}
          {...bottomActionBar}
        />
      )}
    </>
  );
};

export default PageContainer;

export type PageContainerProps = Props;
